import type { AppArea } from './lastApp';
import { defineStoredJson } from './storage';

/**
 * Where the user was when the app was last on screen, so reopening comes back
 * there — Android ends a suspended PWA without warning.
 *
 * localStorage, not session: surviving the app being CLOSED is the whole point.
 *
 * One slot PER APP, because the two are entered separately (the installed PWA
 * opens straight at `/field`, the office at `/`), and scoped to the user id,
 * because a shared field tablet gets handed around: the next person must not be
 * dropped into a project they may not even have access to.
 */

interface StoredLocations {
    userId: string;
    office?: string;
    field?: string;
}

/**
 * The paths in here are fed to the router, so an entry without an owner is no
 * entry at all (`rememberedLocation` checks the path itself as well).
 */
const locations = defineStoredJson<StoredLocations | null>({
    key: 'last_location',
    fallback: () => null,
    decode: (parsed) =>
        parsed &&
        typeof parsed === 'object' &&
        typeof (parsed as StoredLocations).userId === 'string'
            ? (parsed as StoredLocations)
            : undefined
});

export function areaOfPath(pathname: string): AppArea | null {
    const first = pathname.split('/')[1];
    return first === 'office' || first === 'field' ? first : null;
}

/**
 * The list page a record editor sits on — where a restore lands when the record
 * is gone. The category/tab segment and everything above it: the editors are
 * child ROUTES of their grid page, so `…/findings/7/planums/3/edit` belongs to
 * the Befunde page, not to a `…/findings/7` page that does not exist.
 */
function listPathOf(segments: string[]): string {
    if (segments[0] === 'field') {
        return `/${segments.slice(0, 3).join('/')}`;
    }
    if (segments[1] === 'projects') {
        return `/${segments.slice(0, 4).join('/')}`;
    }
    return `/${segments.slice(0, 2).join('/')}`;
}

/**
 * The path worth coming back to, or null when this one isn't. Refused on
 * purpose: anything outside the two apps, and an app's own landing page — that
 * is where a REFUSED restore lands, and remembering it would overwrite the
 * memory before it has been read. A `new` editor is remembered as the list
 * underneath it, since the unsaved record is gone with the app.
 */
export function restorablePath(pathname: string, search = ''): string | null {
    if (!areaOfPath(pathname)) {
        return null;
    }
    const segments = pathname.split('/').filter(Boolean);
    if (segments[segments.length - 1] === 'new') {
        // The list underneath carries no query of the editor's own.
        return listPathOf(segments);
    }
    if (segments.length < 2) {
        return null;
    }
    return `${pathname}${search}`;
}

/** The RxDB collection behind a record editor's URL segment. */
const RECORD_COLLECTIONS: Record<string, string> = {
    findings: 'findings',
    planums: 'finding_planums',
    profiles: 'finding_profiles',
    finds: 'finds',
    samples: 'samples',
    'overall-descriptions': 'overall_descriptions',
    'area-descriptions': 'area_descriptions',
    photos: 'photos',
    'drawing-sheets': 'drawing_sheets',
    diaries: 'diaries',
    'time-entries': 'timeEntries',
    'equipment-time-entries': 'timeEntries'
};

/** What has to still hold for a remembered path to be worth restoring. */
export interface RestoreTarget {
    path: string;
    /** The Maßnahme it is in — must still exist and still be ours. */
    projectId: string | null;
    /** The field category, for the permission check. Null outside /field. */
    category: string | null;
    /** The record an editor URL stands on — must still exist. */
    record: { collection: string; id: string } | null;
    /** Where to land when that record is gone: the list it sits on. */
    listPath: string;
}

export function parseRestorePath(path: string): RestoreTarget {
    const pathname = path.split('?')[0];
    const segments = pathname.split('/').filter(Boolean);
    const field = segments[0] === 'field';

    let projectId: string | null = null;
    if (field && segments.length >= 2) {
        projectId = segments[1];
    } else if (segments[1] === 'projects' && segments.length >= 3) {
        projectId = segments[2];
    }

    // …/<plural>/<id>/edit — the innermost record, which is the one the URL is
    // actually about. A Planum outliving its Befund is not a case that happens.
    let record: RestoreTarget['record'] = null;
    if (segments[segments.length - 1] === 'edit' && segments.length >= 4) {
        const collection = RECORD_COLLECTIONS[segments[segments.length - 3]];
        if (collection) {
            record = { collection, id: segments[segments.length - 2] };
        }
    }

    return {
        path,
        projectId,
        category: field && segments.length >= 3 ? segments[2] : null,
        record,
        listPath: listPathOf(segments)
    };
}

export function rememberLocation(userId: string, path: string): void {
    const area = areaOfPath(path);
    if (!area) {
        return;
    }
    const stored = locations.read();
    // A different user on a shared device starts the record over — keeping the
    // other app's slot would hand them somebody else's place in it.
    const base: StoredLocations =
        stored && stored.userId === userId ? stored : { userId };
    // Storage disabled: the app simply opens on its landing page next time.
    locations.write({ ...base, [area]: path });
}

/**
 * Null when there is nothing to go back to — including when the entry belongs
 * to a different user of a shared tablet.
 */
export function rememberedLocation(
    userId: string,
    area: AppArea
): string | null {
    const stored = read();
    if (!stored || stored.userId !== userId) {
        return null;
    }
    const path = stored[area];
    return typeof path === 'string' && areaOfPath(path) === area ? path : null;
}

function read(): StoredLocations | null {
    return locations.read();
}
